import type { FailureKind, ReadinessLayer, ReadinessResultStatus } from '@lg/shared';

/**
 * Upgrade campaign comparison (build update 2.4, section 10). Pure: the same baseline
 * and candidate results always classify the same way.
 */

export type CaseClassification =
  'Regression' | 'StillFailing' | 'Fixed' | 'NewCase' | 'Skipped' | 'Unchanged';

export interface ComparableResult {
  caseId: string;
  company: string;
  layer: ReadinessLayer;
  template: string;
  status: ReadinessResultStatus;
  failureKind?: FailureKind | null;
  errorSummary?: string | null;
  traceUrl?: string | null;
}

export interface CaseComparison {
  caseId: string;
  company: string;
  layer: ReadinessLayer;
  template: string;
  before: ReadinessResultStatus | null;
  after: ReadinessResultStatus;
  classification: CaseClassification;
  /** For failures after the update: a control not found (UI change) or a failed assertion (behavior). */
  failureKind: FailureKind | null;
  errorSummary: string | null;
  traceUrl: string | null;
}

export function classify(
  before: ReadinessResultStatus | null,
  after: ReadinessResultStatus,
): CaseClassification {
  if (before === null) return 'NewCase';
  // Not run because a case it depends on failed: the cause is reported on that case.
  if (after === 'skipped') return 'Skipped';
  if (before === 'passed' && after === 'failed') return 'Regression';
  if (before === 'failed' && after === 'failed') return 'StillFailing';
  if (before === 'failed' && after === 'passed') return 'Fixed';
  return 'Unchanged';
}

export interface CampaignComparison {
  cases: CaseComparison[];
  counts: Record<CaseClassification, number>;
  /** company × layer: regressions per cell (0 = clean) */
  matrix: Array<{ company: string; layers: Record<ReadinessLayer, { cases: number; regressions: number }> }>;
}

export function compareRuns(
  baseline: readonly ComparableResult[],
  candidate: readonly ComparableResult[],
): CampaignComparison {
  const before = new Map(baseline.map((r) => [`${r.company}|${r.caseId}`, r]));
  const cases: CaseComparison[] = candidate.map((r) => {
    const b = before.get(`${r.company}|${r.caseId}`);
    const classification = classify(b?.status ?? null, r.status);
    return {
      caseId: r.caseId,
      company: r.company,
      layer: r.layer,
      template: r.template,
      before: b?.status ?? null,
      after: r.status,
      classification,
      failureKind: r.status === 'failed' ? (r.failureKind ?? 'Behavior') : null,
      errorSummary: r.status === 'failed' ? (r.errorSummary ?? null) : null,
      traceUrl: r.traceUrl ?? null,
    };
  });
  const counts: CampaignComparison['counts'] = {
    Regression: 0,
    StillFailing: 0,
    Fixed: 0,
    NewCase: 0,
    Skipped: 0,
    Unchanged: 0,
  };
  for (const c of cases) counts[c.classification] += 1;
  const companies = [...new Set(cases.map((c) => c.company))].sort();
  const matrix = companies.map((company) => {
    const layers = {} as CampaignComparison['matrix'][number]['layers'];
    for (const layer of ['L1', 'L2', 'L3', 'L4'] as const) {
      const inCell = cases.filter((c) => c.company === company && c.layer === layer);
      layers[layer] = {
        cases: inCell.length,
        regressions: inCell.filter((c) => c.classification === 'Regression').length,
      };
    }
    return { company, layers };
  });
  return { cases, counts, matrix };
}
